import { useSyncExternalStore } from "react";
import { ChatRuntimeState, KNOWN_CONTENT_TYPES } from "../../types/chat";
import { ChatDataProps, ChatMessage } from "../../types/backend/SengiLabsTypes";
import { PortalMessage, PortalMessageContentPart } from "../../apis/ApiPortalBackend";

const DEFAULT_CHAT_RUNTIME_STATE: ChatRuntimeState = {
    state: null,
    error: null,
    unread: false,
    messageRevision: 0,
    streamingText: null
};
const chatRuntimeStates = new Map<string, ChatRuntimeState>();

// Laufende Streams je Conversation - fuer den Stop-Button, der auch nach einem Chat-Wechsel noch
// den richtigen Request abbrechen muss.
const chatAbortControllers = new Map<string, AbortController>();
export const registerChatAbort = (chatID: string, controller: AbortController) => {
    chatAbortControllers.set(chatID, controller);
};
export const clearChatAbort = (chatID: string, controller: AbortController) => {
    if (chatAbortControllers.get(chatID) === controller) chatAbortControllers.delete(chatID);
};
export const abortChatRun = (chatID: string) => {
    chatAbortControllers.get(chatID)?.abort();
};
const chatRuntimeListeners = new Set<() => void>();
let chatRuntimeRevision = 0;

// The chat history sidebar (ChatHistory.tsx) only ever produces real, persisted PortalBackend
// conversation ids since 2026-07-16 (Task #86/#55) - the previous "chat-<n>" fake-id convention
// is gone, so any non-empty chatID is a real conversation id now.
export const isPersistedConversationId = (chatID: string) => chatID !== "";
export const toContentType = (type: string): ChatDataProps['type'] => (KNOWN_CONTENT_TYPES as string[]).includes(type) ? (type as ChatDataProps['type']) : 'text';

// PortalBackend liefert contentParts (live geprueft 2026-09-21) als JSON-STRING statt als Array.
// Ohne Parsen fiel jede geladene Nachricht auf den flachen message.content zurueck - dort fehlen die
// Code-Zaeune, Code-Bloecke wurden nach jedem Reload zu Fliesstext (codeClass nutzlos). Beide Formen
// akzeptieren; alles andere -> null (Fallback auf content).
const normalizeContentParts = (raw: unknown): PortalMessageContentPart[] | null => {
    let value = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return null;
        }
    }
    return Array.isArray(value) ? (value as PortalMessageContentPart[]) : null;
};

export const toChatMessage = (message: PortalMessage): ChatMessage => {
    // Defensiv: nur mappen, wenn contentParts (ggf. nach dem Parsen) wirklich ein Array ist - sonst
    // crasht ".map is not a function" und die History laedt nicht ("Unable to load history").
    // Fallback: der flache message.content.
    const parts = normalizeContentParts(message.contentParts);
    const contents: ChatDataProps[] = parts && parts.length > 0
        ? parts.map((part, index) => ({
            type: toContentType(part.type),
            element: part.content,
            position: index,
            codeClass: part.codeClass,
        }))
        : [{ type: 'text', element: message.content, position: 0 }];

    return new ChatMessage(message.id, message.role, contents);
};

export const toBackendContent = (entry: ChatMessage): { content: string; contentParts: PortalMessageContentPart[] } => {
    const sortedContents = [...entry.contents].sort((a, b) => a.position - b.position);
    return {
        content: sortedContents.map(c => c.element).join('\n'),
        contentParts: sortedContents.map(c => ({ type: c.type, content: c.element, codeClass: c.codeClass })),
    };
};

export const emitChatRuntimeChange = () => {
    chatRuntimeRevision += 1;
    chatRuntimeListeners.forEach(listener => {
        listener();
    });
};

export const getChatRuntimeState = (chatID: string): ChatRuntimeState => {
    if (!chatID) {
        return DEFAULT_CHAT_RUNTIME_STATE;
    }

    return (chatRuntimeStates.get(chatID) ?? DEFAULT_CHAT_RUNTIME_STATE);
};

export const updateChatRuntimeState = (chatID: string, patch: Partial<ChatRuntimeState>) => {
    if (!chatID) return;
    const current = getChatRuntimeState(chatID);
    chatRuntimeStates.set(chatID, {
        ...current,
        ...patch
    });
    emitChatRuntimeChange();
};

export const markChatAsRead = (chatID: string) => {
    if (!chatID) return;
    const current = getChatRuntimeState(chatID);
    if (!current.unread) return;
    chatRuntimeStates.set(chatID, {
        ...current,
        unread: false
    });
    emitChatRuntimeChange();
};

export const subscribeChatRuntime = (listener: () => void) => {
    chatRuntimeListeners.add(listener);
    return () => {
        chatRuntimeListeners.delete(listener);
    };
};

export const useChatRuntimeState = (chatID: string) => {
    useSyncExternalStore(
        subscribeChatRuntime,
        () => chatRuntimeRevision,
        () => chatRuntimeRevision
    );
    return getChatRuntimeState(chatID);
};

export const useChatRuntimeRevision = () => {
    return useSyncExternalStore(
        subscribeChatRuntime,
        () => chatRuntimeRevision,
        () => chatRuntimeRevision
    );
};